import { instance } from '@/dashboard/api/instance';
import { Price } from '@/dashboard/types/price';
import { FullTransaction, Transaction, TransactionUser } from '@/dashboard/types/transaction';
import { Address } from '@/dashboard/types/address';
import { SubscriptionBase } from '@/dashboard/types/subscriptionBase';
import { Discount } from '@/dashboard/types/discount';
import { Workspace } from '@/dashboard/types/workspace';
import { WithManagementSubscription } from '@/dashboard/types/subscription';

export const PaymentApi = {
  createAddress: async (countryCode: string, postalCode: string): Promise<AddressResponse> => {
    const formData = new FormData();
    formData.append('country_code', countryCode);
    formData.append('postal_code', postalCode);
    const response = await instance.post(`/app/payment/create_address`, formData);
    return response.data;
  },
  getAddress: async (): Promise<AddressResponse> => {
    const response = await instance.post(`/app/payment/get_address`);
    return response.data;
  },
  createTransactionPreview: async (priceId: string, workspaceId: number, addressId: string, discountId?: string): Promise<TransactionResponse> => {
    const formData = createTransactionData(priceId, workspaceId, addressId, discountId);
    const response = await instance.post(`/app/payment/create_transaction_preview`, formData);
    return response.data;
  },
  createTransaction: async (priceId: string, workspaceId: number, addressId: string, discountId?: string): Promise<TransactionResponse> => {
    const formData = createTransactionData(priceId, workspaceId, addressId, discountId);
    const response = await instance.post(`/app/payment/create_transaction`, formData);
    return response.data;
  },
  getSubscriptions: async (): Promise<SubscriptionBase[]> => {
    const response = await instance.get(`/app/payment/get_all_subscriptions`);
    return response.data.subscriptions;
  },
  getPrices: async (): Promise<Price[]> => {
    const response = await instance.get(`/app/payment/get_all_prices`);
    return response.data.prices;
  },
  swapSubscription: async (priceId: string, workspaceId: number): Promise<SwapResponse> => {
    const formData = createTransactionData(priceId, workspaceId);
    const response = await instance.post(`/app/payment/change_subscription_plan`, formData);
    return response.data;
  },
  cancelSubscription: async (workspaceId: number): Promise<SuccessResponse> => {
    const formData = new FormData();
    formData.append('workspace_id', `${workspaceId}`);
    const response = await instance.post(`/app/payment/cancel_subscription`, formData);
    return response.data;
  },
  activateSubscription: async (workspaceId: number): Promise<SuccessResponse> => {
    const formData = new FormData();
    formData.append('workspace_id', `${workspaceId}`);
    const response = await instance.post(`/app/payment/activate_subscription`, formData);
    return response.data;
  },
  getDiscount: async (code: string, priceId: string): Promise<SuccessResponse & { discount: Discount }> => {
    const response = await instance.get(`/app/payment/discount`, { params: { code, price_id: priceId } });
    return response.data;
  },
  getWorkspacesSubscription: async (): Promise<WorkspacesSubscriptionResponse> => {
    const response = await instance.get(`/app/payment/workspaces_subscription`);
    return response.data;
  },
  getWorkspaceTransactions: async (workspaceId: number): Promise<SuccessResponse & { transactions: FullTransaction[] }> => {
    const response = await instance.get(`/app/payment/transactions`, { params: { workspace_id: workspaceId } });
    return response.data;
  },
};

function createTransactionData(priceId: string, workspaceId: number, addressId?: string, discountId?: string): FormData {
  const formData = new FormData();
  formData.append('workspace_id', `${workspaceId}`);
  formData.append('price_id', priceId);

  if (discountId) {
    formData.append('discount_id', discountId);
  }

  if (addressId) {
    formData.append('address_id', addressId);
  }

  return formData;
}

type TransactionResponse = {
  success: boolean;
  transaction: Transaction;
  users: TransactionUser[];
};

type AddressResponse = {
  success: boolean;
  address: Address | undefined;
};

type SwapResponse = {
  success: boolean;
  workspace_id: number;
  subscription_id: string;
};

type SuccessResponse = {
  success: boolean;
};

type WorkspacesSubscriptionResponse = SuccessResponse & {
  workspaces: (Omit<Workspace, 'subscription'> & { subscription: WithManagementSubscription })[];
};
